/**
 * Floor Plan Objects
 * Model classes for canvas objects (tables, walls, areas)
 */

/**
 * Base class for all floor plan objects
 */
export class FloorPlanObject {
  constructor (data = {}) {
    this.id = data.id || null
    this.type = data.type || 'object'
    this.x = data.x || 0
    this.y = data.y || 0
    this.width = data.width || 100
    this.height = data.height || 100
    this.rotation = data.rotation || 0
  }

  /**
   * Convert object to plain object for storage
   */
  toJSON () {
    return {
      id: this.id,
      type: this.type,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      rotation: this.rotation
    }
  }

  /**
   * Check if a point is inside this object
   */
  containsPoint (x, y) {
    return (
      x >= this.x &&
      x <= this.x + this.width &&
      y >= this.y &&
      y <= this.y + this.height
    )
  }

  /**
   * Clone this object
   */
  clone () {
    return new FloorPlanObject(this.toJSON())
  }
}

/**
 * Table object on the canvas
 */
export class TableObject extends FloorPlanObject {
  constructor (data = {}) {
    super(data)
    this.type = 'table'
    this.tableId = data.tableId || null // Link to actual table in Firestore
    this.tableName = data.tableName || 'Table'
    this.shape = data.shape || 'circle' // 'circle', 'square', 'rectangle', 'oval', 'corner'
    this.seats = data.seats || 4
    this.cornerAngle = data.cornerAngle !== undefined ? data.cornerAngle : 0 // 0, 90, 180, 270 for corner tables
    this.status = data.status || 'available' // 'available', 'occupied', 'reserved', 'locked'
    this.color = data.color || 'rgba(97, 84, 164, 0.3)' // Purple - inactive (default)
    this.chairColor = data.chairColor || '#8B4513' // Default brown color for chairs
    this.itemCount = data.itemCount || 0
    this.totalAmount = data.totalAmount || 0
    this.lockedBy = data.lockedBy || null
    this.updateTime = data.updateTime || null
    this.shortcut = data.shortcut || null // Keyboard shortcut (number only)
    this.displayShortcut = data.displayShortcut || null // Display shortcut (area letter + number)
    // Don't set zIndex here - it will be set after adding to layer
  }

  toJSON () {
    return {
      ...super.toJSON(),
      tableId: this.tableId,
      tableName: this.tableName,
      shape: this.shape,
      seats: this.seats,
      cornerAngle: this.cornerAngle,
      status: this.status,
      color: this.color,
      chairColor: this.chairColor,
      itemCount: this.itemCount,
      totalAmount: this.totalAmount,
      lockedBy: this.lockedBy,
      updateTime: this.updateTime,
      shortcut: this.shortcut,
      displayShortcut: this.displayShortcut
    }
  }

  /**
   * Update table status from Firestore table data
   */
  updateFromTable (table) {
    if (!table) return

    this.tableName = table.name || this.tableName
    this.itemCount = table.items ? table.items.length : 0
    this.updateTime = table.update_time || null

    // Determine status
    if (table.locked && table.locked_by) {
      this.status = 'occupied'
      this.color = 'rgba(97, 84, 164, 0.95)' // Purple - active
      this.lockedBy = table.locked_by
    } else if (this.itemCount > 0) {
      this.status = 'occupied'
      this.color = 'rgba(97, 84, 164, 0.95)' // Purple - active
    } else {
      this.status = 'available'
      this.color = 'rgba(97, 84, 164, 0.3)' // Purple - inactive
    }

    if (table.items && table.items.length > 0) {
      this.totalAmount = table.items.reduce((sum, item) => {
        if (item.total !== undefined && item.total !== null) {
          return sum + item.total
        }
        const price = item.discountedUnitPrice || item.discount_price || item.unitPrice || item.price || 0
        const quantity = item.quantity || 1
        return sum + (price * quantity)
      }, 0)
    } else {
      this.totalAmount = 0
    }
  }

  /**
   * Get waiting time in milliseconds
   */
  getWaitingTime () {
    if (!this.updateTime) return 0
    return Date.now() - this.updateTime
  }

  /**
   * Get formatted waiting time (HH:MM:SS)
   */
  getFormattedWaitingTime () {
    const diff = this.getWaitingTime()
    const twoD = x => `${Math.floor(x)}`.padStart(2, '0')
    const HH = twoD((diff / (60 * 60 * 1000)) % 24)
    const mm = twoD((diff / (60 * 1000)) % 60)
    const ss = twoD((diff / 1000) % 60)
    return `${HH}:${mm}:${ss}`
  }

  /**
   * Check if point is inside table (considering shape)
   */
  containsPoint (x, y) {
    if (this.shape === 'circle') {
      const centerX = this.x + this.width / 2
      const centerY = this.y + this.height / 2
      const radius = this.width / 2
      const dx = x - centerX
      const dy = y - centerY
      return (dx * dx + dy * dy) <= (radius * radius)
    } else {
      return super.containsPoint(x, y)
    }
  }

  clone () {
    return new TableObject(this.toJSON())
  }
}

/**
 * Wall object (polyline)
 */
export class WallObject extends FloorPlanObject {
  constructor (data = {}) {
    super(data)
    this.type = 'wall'
    this.points = data.points || [] // Array of {x, y} points
    this.thickness = data.thickness || 10
    this.color = data.color || '#666666'
  }

  toJSON () {
    return {
      ...super.toJSON(),
      points: this.points,
      thickness: this.thickness,
      color: this.color
    }
  }

  /**
   * Add a point to the wall
   */
  addPoint (x, y) {
    this.points.push({ x, y })
  }

  /**
   * Get the last point
   */
  getLastPoint () {
    if (this.points.length === 0) return null
    return this.points[this.points.length - 1]
  }

  /**
   * Check if wall is valid (has at least 2 points)
   */
  isValid () {
    return this.points.length >= 2
  }

  /**
   * Calculate bounding box
   */
  getBounds () {
    if (this.points.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 }
    }

    const xs = this.points.map(p => p.x)
    const ys = this.points.map(p => p.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    }
  }

  clone () {
    return new WallObject(this.toJSON())
  }
}

/**
 * Door object (line with arc showing swing direction)
 */
export class DoorObject extends FloorPlanObject {
  constructor (data = {}) {
    super(data)
    this.type = 'door'
    this.width = data.width || 80 // Door width
    this.height = data.height || 10 // Door thickness (for visual/selection)
    this.color = data.color || '#8B4513'
    this.doorSwing = data.doorSwing || 'left-in' // 'left-in', 'left-out', 'right-in', 'right-out'
  }

  toJSON () {
    return {
      ...super.toJSON(),
      color: this.color,
      doorSwing: this.doorSwing
    }
  }

  clone () {
    return new DoorObject(this.toJSON())
  }
}

/**
 * Bar/Counter object
 */
export class BarObject extends FloorPlanObject {
  constructor (data = {}) {
    super(data)
    this.type = 'bar'
    this.barName = data.barName || 'Šank'
    this.shape = data.shape || 'rectangle' // 'rectangle', 'semicircle', 'semioval'
    this.color = data.color || '#8B4513' // Brown color for bar
    this.borderColor = data.borderColor || '#654321'
    this.tableId = data.tableId || null // Link to actual table in Firestore
    this.shortcut = data.shortcut || null // Keyboard shortcut (number only)
    this.displayShortcut = data.displayShortcut || null // Display shortcut (area letter + number)
  }

  toJSON () {
    return {
      ...super.toJSON(),
      barName: this.barName,
      shape: this.shape,
      color: this.color,
      borderColor: this.borderColor,
      tableId: this.tableId,
      shortcut: this.shortcut,
      displayShortcut: this.displayShortcut
    }
  }

  clone () {
    return new BarObject(this.toJSON())
  }
}

/**
 * Text box object (standalone text with formatting)
 */
export class TextBoxObject extends FloorPlanObject {
  constructor (data = {}) {
    super(data)
    this.type = 'textbox'
    this.text = data.text || 'Text'
    this.fontSize = data.fontSize || 16
    this.fontFamily = data.fontFamily || 'Arial'
    this.fontStyle = data.fontStyle || 'normal' // 'normal', 'bold', 'italic', 'bold italic'
    this.textDecoration = data.textDecoration || '' // '', 'underline', 'line-through'
    this.textColor = data.textColor || '#000000'
    this.backgroundColor = data.backgroundColor || '#ffffff'
    this.borderColor = data.borderColor || '#cccccc'
    this.padding = data.padding || 10
    this.align = data.align || 'left' // 'left', 'center', 'right'
  }

  toJSON () {
    return {
      ...super.toJSON(),
      text: this.text,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fontStyle: this.fontStyle,
      textDecoration: this.textDecoration,
      textColor: this.textColor,
      backgroundColor: this.backgroundColor,
      borderColor: this.borderColor,
      padding: this.padding,
      align: this.align
    }
  }

  clone () {
    return new TextBoxObject(this.toJSON())
  }
}

/**
 * Area label object (rectangle with text)
 */
export class AreaObject extends FloorPlanObject {
  constructor (data = {}) {
    super(data)
    this.type = 'area'
    this.label = data.label || 'Area'
    this.backgroundColor = data.backgroundColor || '#f0f0f0'
    this.borderColor = data.borderColor || '#cccccc'
    this.textColor = data.textColor || '#666666'
    this.fontSize = data.fontSize || 16
    this.opacity = data.opacity || 0.5
    this.pattern = data.pattern || 'none' // Pattern for area background
    this.shortcut = data.shortcut || null // Letter shortcut (A, B, C...)
  }

  toJSON () {
    return {
      ...super.toJSON(),
      label: this.label,
      backgroundColor: this.backgroundColor,
      borderColor: this.borderColor,
      textColor: this.textColor,
      fontSize: this.fontSize,
      opacity: this.opacity,
      pattern: this.pattern,
      shortcut: this.shortcut
    }
  }

  clone () {
    return new AreaObject(this.toJSON())
  }
}

/**
 * Factory function to create floor plan object from JSON
 */
export function createFloorPlanObject (data) {
  switch (data.type) {
    case 'table':
      return new TableObject(data)
    case 'wall':
      return new WallObject(data)
    case 'door':
      return new DoorObject(data)
    case 'bar':
      return new BarObject(data)
    case 'textbox':
      return new TextBoxObject(data)
    case 'area':
      return new AreaObject(data)
    default:
      return new FloorPlanObject(data)
  }
}

/**
 * Validate floor plan object
 */
export function validateFloorPlanObject (obj) {
  if (!obj || !obj.id || !obj.type) {
    return { valid: false, error: 'Invalid object: missing id or type' }
  }

  if (obj.type === 'wall') {
    if (!obj.points || obj.points.length < 2) {
      return { valid: false, error: 'Wall must have at least 2 points' }
    }
  }

  if (obj.width < 0 || obj.height < 0) {
    return { valid: false, error: 'Width and height must be positive' }
  }

  return { valid: true }
}

/**
 * Check if two objects overlap
 */
export function objectsOverlap (obj1, obj2) {
  // Simple AABB collision detection
  return !(
    obj1.x + obj1.width < obj2.x ||
    obj2.x + obj2.width < obj1.x ||
    obj1.y + obj1.height < obj2.y ||
    obj2.y + obj2.height < obj1.y
  )
}

/**
 * Snap value to grid
 */
export function snapToGrid (value, gridSize = 20) {
  return Math.round(value / gridSize) * gridSize
}
